import React, { useState, useCallback, useEffect } from 'react';
import { Field, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { Checkbox } from '@atlaskit/checkbox';
import { SelectShipToAddress, AddressFormSection, shipToAddressesType, FieldErrorMap } from '@css-xp/base';
import { CommerceAddress, generatedTypesNew } from '@css-xp/graphtypes';
import { useQuery } from '@apollo/client';
import { fetchShippingAddressesForTransactionAccount } from '../../../../tabs/invoice-group-tabs';
import { FormData } from './types';
import { buildFieldName } from '@atlassian/cssxp-utils-generic';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { getTaxIds } from '@css-xp/base/tax-exempt/src/tax-utils';

interface SoldToAddressStepProps {
  onFieldValueChange: (name: string, value: any) => void;
  transactionAccountId: string;
  billToAddress?: CommerceAddress | null;
  shipToAddress: CommerceAddress | undefined | null;
  onShipToAddressUpdate: (newAddress: CommerceAddress | null | undefined) => void;
  billingProfileData: FormData;
  page?: string;
  fieldErrors?: FieldErrorMap;
  isPartnerAccount?: boolean;
}

const getTaxIdsData = (billingProfileData: FormData) => {
  const taxIdsData = getTaxIds(billingProfileData);
  const taxLabels = taxIdsData?.map((taxData) => ({
    type: taxData?.label,
    displayName: taxData?.displayName,
  }));
  return { taxIds: taxIdsData, taxLabels: taxLabels };
};

const transformFormDataToShippingAddress = (billingProfileData: FormData) => {
  return {
    name: billingProfileData?.soldToName,
    addressLine1: billingProfileData?.soldToAddress1,
    addressLine2: billingProfileData?.soldToAddress2,
    city: billingProfileData?.soldToCity,
    state: billingProfileData?.soldToState?.value,
    country: billingProfileData?.soldToCountry?.value,
    postcode: billingProfileData?.soldToPostalCode,
    ...getTaxIdsData(billingProfileData),
  };
};

export const SoldToAddressStep = ({
  onFieldValueChange,
  transactionAccountId,
  billToAddress,
  shipToAddress,
  onShipToAddressUpdate,
  billingProfileData,
  page,
  fieldErrors,
  isPartnerAccount = false,
}: SoldToAddressStepProps) => {
  const [isChecked, setIsChecked] = useState(false);
  const { data } = useQuery<
    generatedTypesNew.FetchShipToPartiesForTransactionAccountQuery,
    generatedTypesNew.FetchShipToPartiesForTransactionAccountQueryVariables
  >(fetchShippingAddressesForTransactionAccount, {
    variables: { id: transactionAccountId },
    fetchPolicy: 'no-cache',
  });
  useEffect(() => {
    /*
      billingProfileData is form data which needs to be updated as shipToAddress data in case of
       transaction account page only and because it involves moving back and forth between steps
      */
    if (page === 'transaction-account' && !isEmpty(billingProfileData)) {
      onShipToAddressUpdate(transformFormDataToShippingAddress(billingProfileData));
    }
  }, [billingProfileData]);

  const onSameAsBillTo = useCallback(() => {
    // check if available ship to addresses already have a address similar to bill to address
    const billToParty = {
      name: billToAddress?.name,
      taxId: billToAddress?.taxId || null,
      taxIds: billToAddress?.taxIds?.length ? billToAddress?.taxIds : null,
      postalAddress: {
        line1: billToAddress?.addressLine1,
        line2: billToAddress?.addressLine2,
        city: billToAddress?.city,
        country: billToAddress?.country,
        state: billToAddress?.state,
        postcode: billToAddress?.postcode,
      },
    };
    const hasSameShipToParty = data?.transactionAccount.availableShipToParties?.nodes?.find((node) => {
      const shipToParty = {
        name: node?.name,
        taxId: node?.taxId || null,
        taxIds: node?.taxIds?.length ? node?.taxIds : null,
        postalAddress: {
          line1: node?.addressLine1,
          line2: node?.addressLine2,
          city: node?.city,
          country: node?.country,
          state: node?.state,
          postcode: node?.postcode,
        },
      };

      return isEqual(shipToParty, billToParty);
    });

    // if bill to address is already present in available ship to parties then set shipTo equal to the matched shipToPartyId else set the shipTo equal to our bill to address
    onShipToAddressUpdate(isChecked ? {} : hasSameShipToParty ? hasSameShipToParty : billToAddress);
    setIsChecked((current) => !current);
  }, [billToAddress, isChecked, onShipToAddressUpdate, data]);

  const updateMultiFormData = useCallback(
    (fieldValues: Record<string, any>) => {
      Object.keys(fieldValues).forEach((fieldKey) => {
        onFieldValueChange(fieldKey, fieldValues[fieldKey]);
      });
    },
    [onFieldValueChange]
  );

  const onSelect = useCallback(
    (selected: any) => {
      onShipToAddressUpdate(selected?.value);

      // Update field values on change
      updateMultiFormData({
        [buildFieldName('Sold to', 'name')]: selected?.value?.name,
        [buildFieldName('Sold to', 'address1')]: selected?.value?.addressLine1,
        [buildFieldName('Sold to', 'address2')]: selected?.value?.addressLine2,
        [buildFieldName('Sold to', 'city')]: selected?.value?.city,
        [buildFieldName('Sold to', 'postcode')]: selected?.value?.postcode,
        [buildFieldName('Sold to', 'taxId')]: selected?.value?.taxId,
      });
    },
    [onShipToAddressUpdate, updateMultiFormData]
  );

  useEffect(() => {
    if (shipToAddress && !isEmpty(shipToAddress)) {
      // Update field values on change
      updateMultiFormData({
        [buildFieldName('Sold to', 'name')]: shipToAddress.name,
        [buildFieldName('Sold to', 'address1')]: shipToAddress.addressLine1,
        [buildFieldName('Sold to', 'address2')]: shipToAddress.addressLine2,
        [buildFieldName('Sold to', 'city')]: shipToAddress.city,
        [buildFieldName('Sold to', 'postcode')]: shipToAddress.postcode,
        [buildFieldName('Sold to', 'taxId')]: shipToAddress.taxId,
      });
    }
  }, [shipToAddress, updateMultiFormData]);

  return (
    <>
      <FormSection>
        <div className="form-component-width">
          <h3>{billingProfileData.invoiceGroupName}</h3>

          <Field
            name="invoiceGroupName"
            id="invoiceGroupName"
            defaultValue={billingProfileData.invoiceGroupName}
            label="Name"
            isRequired={true}
          >
            {({ fieldProps }) => <Textfield {...fieldProps} name="invoiceGroupName" testId="invoice-group-name" />}
          </Field>
        </div>
      </FormSection>
      <FormSection>
        <div className="form-component-width">
          <h3 className="sold-to-address-header">Sold-to address</h3>
          <Checkbox
            isChecked={isChecked}
            onChange={onSameAsBillTo}
            label="Copy as billing address"
            value="Controlled Checkbox"
            name="controlled-checkbox"
            data-testid="same-as-bill"
          />
          <SelectShipToAddress shipToAddresses={data as shipToAddressesType} onSelect={onSelect} />
          <AddressFormSection
            {...shipToAddress}
            isEditable={true}
            addressName="Sold to"
            setFieldValue={onFieldValueChange}
            isRequired={isPartnerAccount}
            taxIdAddressPrefix={'Sold-to'}
            isChecked={isChecked}
            fieldErrors={fieldErrors}
          />
        </div>
      </FormSection>
    </>
  );
};
